// src/pages/sqf/SqfSchedulePage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';

const API = process.env.REACT_APP_API_URL || '';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const displayName = (u) => (u?.firstName || u?.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : (u?.username || '—');

const FREQUENCY_LABELS = {
  daily:     'Daily',
  per_shift: 'Per Shift',
  weekly:    'Weekly',
  monthly:   'Monthly',
  on_demand: 'On-Demand',
};

const EMPTY_FORM = {
  label: '', frequency: 'daily', shiftsPerDay: 1,
  shiftStartTimes: '', dueOffsetMinutes: 60,
  assignedUserId: '', notifyMinutesBefore: 30,
  startDate: '',
};

export default function SqfSchedulePage() {
  const { id } = useParams();
  const { lang } = useLanguage();
  const [template, setTemplate] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      sqfService.getTemplate(id),
      sqfService.getSchedules(),
      axios.get(`${API}/api/users`, authHeaders()).then(r => r.data),
    ]).then(([tmpl, allSchedules, allUsers]) => {
      setTemplate(tmpl);
      setSchedules(allSchedules.filter(s => s.templateId === parseInt(id, 10)));
      setUsers(Array.isArray(allUsers) ? allUsers : []);
    }).catch(() => setError(t('Failed to load schedule data.', 'Error al cargar los datos del horario.', lang)))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        templateId: id,
        label: form.label || null,
        frequency: form.frequency,
        shiftsPerDay: parseInt(form.shiftsPerDay, 10) || 1,
        shiftStartTimes: form.shiftStartTimes
          ? form.shiftStartTimes.split(',').map(s => s.trim()).filter(Boolean)
          : null,
        dueOffsetMinutes: parseInt(form.dueOffsetMinutes, 10) || 60,
        assignedUserId: form.assignedUserId ? parseInt(form.assignedUserId, 10) : null,
        notifyMinutesBefore: parseInt(form.notifyMinutesBefore, 10) || 30,
        startDate: form.startDate || null,
      };
      const schedule = await sqfService.createSchedule(payload);
      setSchedules(s => [...s, schedule]);
      setShowForm(false);
      setForm(EMPTY_FORM);
    } catch {
      setError(t('Failed to save schedule.', 'Error al guardar el horario.', lang));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (scheduleId) => {
    if (!window.confirm(t('Deactivate this schedule? No new instances will be generated.', '¿Desactivar este horario? No se generarán nuevas instancias.', lang))) return;
    try {
      await sqfService.deleteSchedule(scheduleId);
      setSchedules(s => s.filter(sc => sc.id !== scheduleId));
    } catch {
      setError(t('Failed to deactivate schedule.', 'Error al desactivar el horario.', lang));
    }
  };

  if (loading) return <div className="container mt-4 text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>;

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf/templates" className="text-muted" style={{ fontSize: 12 }}>← {t('Templates', 'Plantillas', lang)}</Link>
          <h4 className="mb-0 mt-1">{template?.name} — {t('Schedule', 'Horario', lang)}</h4>
        </div>
        <div className="d-flex gap-2">
          <Link to="/sqf/schedules" className="btn btn-outline-secondary btn-sm">{t('All Schedules', 'Todos los Horarios', lang)}</Link>
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ {t('Add Schedule', 'Agregar Horario', lang)}</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {schedules.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p>{t('No schedules yet. This checklist must be triggered manually or on-demand.', 'Aún no hay horarios. Esta lista de verificación debe activarse manualmente o bajo demanda.', lang)}</p>
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ {t('Add Schedule', 'Agregar Horario', lang)}</button>
        </div>
      ) : (
        <div className="table-responsive mb-4">
          <table className="table table-sm table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th>{t('Label', 'Etiqueta', lang)}</th>
                <th>{t('Frequency', 'Frecuencia', lang)}</th>
                <th>{t('Start Date', 'Fecha de Inicio', lang)}</th>
                <th>{t('Due Offset', 'Tiempo Límite', lang)}</th>
                <th>{t('Assigned To', 'Asignado a', lang)}</th>
                <th>{t('Remind', 'Recordatorio', lang)}</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id}>
                  <td>{s.label || <span className="text-muted">—</span>}</td>
                  <td>{FREQUENCY_LABELS[s.frequency] || s.frequency}</td>
                  <td style={{ fontSize: 12 }}>{s.startDate || <span className="text-muted">{t('Immediately', 'Inmediatamente', lang)}</span>}</td>
                  <td style={{ fontSize: 12 }}>{s.dueOffsetMinutes} {t('min after start', 'min después del inicio', lang)}</td>
                  <td style={{ fontSize: 12 }}>
                    {s.AssignedUser ? displayName(s.AssignedUser) : <span className="text-muted">{t('Unassigned', 'Sin asignar', lang)}</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{s.notifyMinutesBefore} {t('min before', 'min antes', lang)}</td>
                  <td className="text-end">
                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeactivate(s.id)}>{t('Deactivate', 'Desactivar', lang)}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add schedule form */}
      {showForm && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('New Schedule', 'Nuevo Horario', lang)}</h5>
                <button className="btn-close" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} />
              </div>
              <div className="modal-body">

                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Label', 'Etiqueta', lang)} <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({t('optional — e.g. "Walk-in Freezer #1"', 'opcional — ej. "Congelador #1"', lang)})</span></label>
                  <input className="form-control" value={form.label} onChange={e => set('label', e.target.value)} />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Frequency', 'Frecuencia', lang)}</label>
                  <select className="form-select" value={form.frequency} onChange={e => set('frequency', e.target.value)}>
                    {Object.entries(FREQUENCY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>

                {form.frequency === 'per_shift' && (
                  <>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">{t('Shifts per Day', 'Turnos por Día', lang)}</label>
                      <input type="number" className="form-control" min={1} max={3}
                        value={form.shiftsPerDay} onChange={e => set('shiftsPerDay', e.target.value)} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">{t('Shift Start Times', 'Horarios de Inicio de Turno', lang)} <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({t('comma-separated, e.g. 06:00, 14:00', 'separados por coma, ej. 06:00, 14:00', lang)})</span></label>
                      <input className="form-control" value={form.shiftStartTimes}
                        onChange={e => set('shiftStartTimes', e.target.value)} placeholder="06:00, 14:00, 22:00" />
                    </div>
                  </>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Start Date', 'Fecha de Inicio', lang)} <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({t('optional — leave blank to start immediately', 'opcional — dejar en blanco para iniciar inmediatamente', lang)})</span></label>
                  <input type="date" className="form-control" value={form.startDate}
                    onChange={e => set('startDate', e.target.value)} />
                  {form.startDate && (
                    <div className="form-text">{t('First instance will be generated on', 'La primera instancia se generará el', lang)} {form.startDate}.</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Due Offset (minutes after shift start)', 'Tiempo Límite (minutos después del inicio del turno)', lang)}</label>
                  <input type="number" className="form-control" value={form.dueOffsetMinutes}
                    onChange={e => set('dueOffsetMinutes', e.target.value)} />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Assign To', 'Asignar a', lang)} <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({t('optional', 'opcional', lang)})</span></label>
                  <select className="form-select" value={form.assignedUserId} onChange={e => set('assignedUserId', e.target.value)}>
                    <option value="">— {t('Unassigned', 'Sin asignar', lang)} —</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {displayName(u)} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Remind (minutes before due)', 'Recordatorio (minutos antes del vencimiento)', lang)}</label>
                  <input type="number" className="form-control" value={form.notifyMinutesBefore}
                    onChange={e => set('notifyMinutesBefore', e.target.value)} />
                </div>

              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>{t('Cancel', 'Cancelar', lang)}</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? t('Saving…', 'Guardando…', lang) : t('Save Schedule', 'Guardar Horario', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
